import {
  Box,
  Button,
  CircularProgress,
  Pagination,
  Paper,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  TextField,
} from "@mui/material";
import { observer } from "mobx-react";
import userRoleStore from "../api";
import { ReactNode, useEffect, useState } from "react";
import loadingStore from "core/utils/loading";
import { useNavigate } from "react-router";
import { useAuth } from "app/authHooks";
import { Roles } from "../model";
import Header from "components/Header";
import { hasPermission, Permission } from "core/utils/roles";
import UserRolesRow from "./user-roles-row";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const TablePlaceholder: React.FC<{ children: ReactNode }> = ({ children }) => (
  <TableRow>
    <TableCell colSpan={9} align="center">
      {children}
    </TableCell>
  </TableRow>
);

function UserRolesTable() {
  const { attributes } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [pageSize] = useState(10);
  const [isExpanded, setIsExpanded] = useState(false);
  const [roles, setRoles] = useState<Roles[]>([]);

  const { getUserRoles } = userRoleStore;
  const { loading } = loadingStore;

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      onInit(searchText, currentPage);
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [searchText, currentPage]);

  const onInit = async (searchText: string, page: number) => {
    try {
      const roles = await getUserRoles({
        pageIndex: page - 1,
        pageSize: pageSize,
        searchText: searchText,
      });
      setRoles(roles?.data || []);
      setTotalPages(Math.ceil((roles?.total || 0) / pageSize));
    } catch (error) {
      console.error("Failed to fetch roles:", error);
    }
  };

  return (
    <Box sx={{ paddingBottom: "64px" }}>
      <Box display="flex" justifyContent="space-between">
        <Header title="User Roles" subtitle="Manage User Roles" />
        {hasPermission("create:roles" as Permission) && (
          <Box
            display="flex"
            flexDirection="row"
            gap="10px"
            alignItems="center"
          >
            <Button
              type="submit"
              color="primary"
              variant="contained"
              onClick={() => navigate("/security/roles/add-edit")}
            >
              Add User Roles
            </Button>
          </Box>
        )}
      </Box>

      <Box py="20px">
        <Accordion
          expanded={isExpanded}
          onChange={() => setIsExpanded(!isExpanded)}
        >
          <AccordionSummary
            sx={{ "&.Mui-expanded": { minHeight: "32px" } }}
            expandIcon={<ExpandMoreIcon />}
          >
            <Typography>Filters</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: "16px" }}>
            <Box
              display="flex"
              gap="16px"
              flexDirection="column"
              alignItems="flex-start"
            >
              <TextField
                fullWidth
                label="Enter name, description or customer id"
                variant="outlined"
                sx={{ flex: 1 }}
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setCurrentPage(1);
                }}
              />

              <Box display="flex" alignItems="center" gap="8px" width="100%">
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    setSearchText("");
                    setCurrentPage(1);
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>

      <TableContainer component={Paper}>
        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow>
              {[
                { field: "name", label: "Name" },
                { field: "description", label: "Description" },
                { field: "customer_id", label: "Customer Id" },
                { field: "role_rank", label: "Rank" },
                { field: "created_at", label: "Created At" },
              ].map(({ field, label }) => (
                <TableCell key={field}>
                  <Typography noWrap variant="h5">
                    {label}{" "}
                  </Typography>
                </TableCell>
              ))}
              <TableCell>
                <Typography noWrap variant="h5">
                  Action
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TablePlaceholder>
                <CircularProgress />
              </TablePlaceholder>
            )}
            {!loading && !roles?.length && (
              <TablePlaceholder>
                <Typography variant="h5">No Data</Typography>
              </TablePlaceholder>
            )}
            {!loading &&
              roles?.map((row, index) => (
                <UserRolesRow row={row} key={index} attributes={attributes} />
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          right: 0,
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          padding: "10px",
          boxShadow: "0px -2px 10px rgba(0, 0, 0, 0.1)",
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Pagination
          className="my-3"
          count={totalPages}
          page={currentPage}
          variant="outlined"
          onChange={(event, value) => setCurrentPage(value)}
        />
      </Box>
    </Box>
  );
}

export default observer(UserRolesTable);
